import React, { useEffect, useState, useCallback, useRef } from "react";

import useOutsideClick from "../../hooks/useOutsideClick";
import { useMessage } from "../../context/MessageContext";
import { GENERAL_ERROR_MESSAGE } from "../../utils/message";
import { getApi } from "../../apis/api";

import { XCrossIcon } from "../../assets";

import "../../assets/scss/abstracts/_form.scss";
import { CUSTOMER_TYPE, VALIDATION_FOCUS_ATTR } from "../../utils/constants";

interface MutliDropdownType {
  columns: ColumnTypes[];
  captionTitles?: string;
  data: any[];
  setData: (data: any) => void;
  rowData: any;
  setRowData: (row: any) => void;
  displayColumn: string;
  agentId?: string;
  type?: string;
  apiUrl: string;
  limit?: number;
  setAgentRowData?: (data: any) => void;
  agentFind?: boolean;
  disable?: boolean;
  enableRemove?: boolean;
  labelTitle?: string;
  mandatory?: boolean;
  checkInputChange?: () => void;
  parentCustomerId?: string;
  skipApiCallWhenNoParent?: boolean; // control API call based on parentCustomerId
  extendedUrl?: Record<string, string>; // Additional key-value pairs to append to the API URL
  status?: number;
  extraParentId?: {
    key: string;
    id: string;
  };
  from?: string; // To identify the source of the dropdown
  setFieldValue?: (field: string, value: any) => void;
  extraFields?: string[];
  initialSearchKeyword?: string;
  /**
   * Identifies this dropdown to the mandatory-field validation, which locates controls
   * by `data-field` and focuses the caption when a required selection is missing.
   */
  fieldKey?: string;
  /** Validation message rendered beneath the control. */
  error?: string;
  /**
   * A pre-fetched list to search entirely client-side, for reference data small
   * enough to load once rather than page through — pass `apiUrl=""` alongside
   * this so the API-driven path (unused here) stays inert. Search matches the
   * typed text against every column's value, not just `displayColumn`, the
   * same way the rendered list shows every column. Every other prop (`limit`,
   * `parentCustomerId`, pagination, …) is ignored while this is set.
   */
  staticData?: any[];
}

interface ColumnTypes {
  key: string;
  title: string;
}

const MutliDropdown: React.FC<MutliDropdownType> = ({
  columns,
  captionTitles = "Select",
  data,
  setData,
  rowData,
  setRowData,
  displayColumn,
  agentId,
  type,
  apiUrl,
  limit = 15,
  setAgentRowData,
  agentFind = false,
  disable,
  enableRemove,
  labelTitle,
  mandatory,
  checkInputChange,
  parentCustomerId,
  skipApiCallWhenNoParent,
  extendedUrl,
  status = 1,
  extraParentId,
  from,
  setFieldValue,
  extraFields,
  initialSearchKeyword,
  fieldKey,
  error,
  staticData,
}) => {
  const { setMessage } = useMessage();
  const [isSelected, setIsSelected] = useState<number | null>(null);
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isSearchVisible, setIsSearchVisible] = useState<boolean>(false);
  const [isDivFocused, setIsDivFocused] = useState(false);
  const isFetching = useRef(false);
  const captionRef = useRef<HTMLDivElement>(null);
  /** One-shot guard so the focus handed back to the caption after an Enter selection doesn't re-open the list. */
  const skipFocusOpenRef = useRef(false);
  /** Limits the open-time highlight to once per open cycle, so it doesn't fight the arrow keys. */
  const hasAutoHighlightedRef = useRef(false);
  /**
   * Latest `columns`/`setData`, read (not depended on) by the static-search
   * effect below. Many callers pass these as fresh references every render —
   * an inline array literal for `columns`, a wrapping arrow function for
   * `setData` — so listing either in that effect's dependency array would
   * re-run it, and refetch, on every render of the parent, for every existing
   * caller, not just the one this was built for. Refs sidestep that: the
   * effect still always acts on the current value, without re-triggering when
   * it changes.
   */
  const columnsRef = useRef(columns);
  columnsRef.current = columns;
  const setDataRef = useRef(setData);
  setDataRef.current = setData;


  /**
   * Opens the dropdown from a caption click. Deliberately open-only rather than a toggle:
   * the caption renders only while the dropdown is closed, and focus now opens the list
   * before the click lands, so toggling here would close again what focus just opened.
   */
  const handleOpened = () => {
    if (disable) {
      return;
    }
    setIsOpened(true);
    setIsSearchVisible(true);
  }
  /**
   * Opens the dropdown and enables the search field.
   */
  const handleDoubleClick = () => {
    if (disable) {
      return;
    }
    setIsOpened(true);
    setIsSearchVisible(true);
  };

  /**
   * Custom hook to handle clicks outside the dropdown, closing it.
   */
  const outsideClickRef = useOutsideClick<HTMLDivElement>(() => {
    setIsOpened(false);
    setIsSearchVisible(false);
    setIsDivFocused(false);
  });

  /**
   * Commits a row as the selection and returns the control to its resting state.
   * Shared by the row click and the Enter key so both paths leave the same state behind.
   *
   * @param {any} item - The row to select.
   * @param {number} index - Its position in `data`. Retained as the highlight so the
   *   `rowData.location` re-sync effect below still has an index to work from.
   * @param {boolean} refocusCaption - Hand focus back to the caption, for the keyboard paths.
   */
  const commitSelection = (
    item: any,
    index: number,
    refocusCaption: boolean = false
  ) => {
    setRowData(item);
    setIsSelected(index);
    setIsOpened(false);
    setIsSearchVisible(false);
    setKeyword("");
    checkInputChange && checkInputChange();
    if (!refocusCaption) return;
    skipFocusOpenRef.current = true;
    setTimeout(() => {
      if (captionRef.current) {
        captionRef.current.setAttribute("data-suppress-focus", "");
        captionRef.current.focus();
      }
    }, 0);
  };

  /**
   * Handles keyboard navigation and selection within the dropdown.
   *
   * @param {React.KeyboardEvent} e - The keyboard event.
   */

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpened) return;
    // `setData(null)` is a reachable state, so never index `data` directly.
    const items = data || [];

    switch (e.key) {
      case "Tab": {
        // Close the list and move focus on to the next control, so leaving the
        // field doesn't cost a second Tab.
        e.preventDefault();
        setIsOpened(false);
        setIsSearchVisible(false);
        setKeyword("");
        setIsSelected(null);
        skipFocusOpenRef.current = true;
        const formElements = Array.from(
          document.querySelectorAll<HTMLElement>(
            'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]:not([disabled])'
          )
        ).filter((el) => el.offsetParent !== null);
        const currentContainer = outsideClickRef.current;
        if (currentContainer) {
          const currentIndex = formElements.findIndex(
            (el) =>
              currentContainer.contains(el) &&
              (el === document.activeElement || el === e.target)
          );
          if (currentIndex !== -1) {
            // Skip past the dropdown's own focusable children.
            for (let i = currentIndex + 1; i < formElements.length; i++) {
              if (!currentContainer.contains(formElements[i])) {
                formElements[i].focus();
                break;
              }
            }
          }
        }
        break;
      }
      case "ArrowDown":
        e.preventDefault();
        if (items.length === 0) return;
        // `>=` rather than `===`: a refetch can leave a stale index past the new end.
        if (isSelected === null || isSelected >= items.length - 1) {
          setIsSelected(0);
        } else {
          setIsSelected((prev) => (prev! + 1) % items.length);
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (items.length === 0) return;
        if (isSelected === null || isSelected <= 0) {
          setIsSelected(items.length - 1);
        } else {
          setIsSelected((prev) => (prev! - 1 + items.length) % items.length);
        }
        break;
      case "Enter":
        e.preventDefault();
        if (isSelected !== null && items[isSelected]) {
          commitSelection(items[isSelected], isSelected, true);
        } else if (keyword && items.length > 0) {
          // Nothing highlighted, so only commit when the typed text names a row exactly.
          const needle = keyword.trim().toLowerCase();
          const matchIndex = items.findIndex(
            (item) =>
              String(item?.[displayColumn] ?? "").trim().toLowerCase() === needle
          );
          if (matchIndex !== -1) {
            commitSelection(items[matchIndex], matchIndex, true);
          }
        }
        break;
      default:
        break;
    }
  };

  /**
   * Handles search input changes and resets pagination.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The input change event.
   */
  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
    // The index points into the pre-filter list, so it is meaningless once results change.
    setIsSelected(null);
    setPage(1);
    setHasMore(true);
  }, []);

  /**
   * Handles removing the selected row data.
   */
  const handleRemoveRowData = () => {
    setRowData(null);
    setIsSelected(null);
    setIsOpened(false);
    // Kept in step with isOpened; leaving it true strands the search input on screen
    // with the caption unmounted. `keyword` is deliberately left alone — clearing it
    // here would retrigger the keyword effect and double-fetch on parent changes.
    setIsSearchVisible(false);
  };

  /**
   * Keeps the keyboard-highlighted row inside the scroll viewport. Scoped to this
   * dropdown's own container, so a second dropdown on the page can't be scrolled instead.
   */
  useEffect(() => {
    if (isSelected !== null && isOpened && dropdownRef.current) {
      const container = dropdownRef.current;
      const highlightedItem = container.querySelector(
        ".highlighted"
      ) as HTMLElement;
      if (highlightedItem) {
        const containerTop = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;
        const itemTop = highlightedItem.offsetTop;
        const itemBottom = itemTop + highlightedItem.clientHeight;

        if (itemTop < containerTop) {
          container.scrollTop = itemTop;
        } else if (itemBottom > containerBottom) {
          container.scrollTop = itemBottom - container.clientHeight;
        }
      }
    }
  }, [isSelected, isOpened]);

  /**
   * Converts the extendedUrl object into a query string.
   */
  const getExtendedUrlParams = () => {
    if (!extendedUrl) return "";
    return Object.entries(extendedUrl)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
      )
      .join("&");
  };

  /**
   * Fetches filtered data from the API based on the search keyword and other parameters.
   *
   * @param {boolean} reset - Whether to reset the current data or append new data.
   */
  const fetchFilteredData = useCallback(
    async (reset: boolean = false) => {
      // if (
      //   isFetching.current ||
      //   !apiUrl ||
      //   isLoading ||
      //   (skipApiCallWhenNoParent && !parentCustomerId)
      // ) {
      //   // removing data when parentCustomerId is not provided
      //   handleRemoveRowData();
      //   setData(null);
      //   return;
      // }
      // Skip early return checks if initialSearchKeyword is provided
      if (!apiUrl) {
        return;
      }
      if (!initialSearchKeyword) {
        if (isFetching.current || isLoading) {
          return;
        }
        if (skipApiCallWhenNoParent && !parentCustomerId) {
          handleRemoveRowData();
          setData(null);
          return;
        }
      }
      isFetching.current = true; // Prevent multiple calls
      try {
        setIsLoading(true);
        let url = `${apiUrl}?limit=${limit}&page=${reset ? 1 : page}`;
        if (initialSearchKeyword) {
          url += `&keyword=${initialSearchKeyword}`;
        }
        if (keyword) {
          url += `&keyword=${keyword}`;
        }
        if (agentId) {
          url += `&agentId=${agentId}`;
        }
        if (type) {
          url += `&type=${type}`;
        }
        if (parentCustomerId) {
          url += `&parentCustomerId=${parentCustomerId}`;
        }
        if (extraParentId?.id) {
          url += `&${extraParentId.key}=${extraParentId.id}`;
        }
        if ([1, 0]?.includes(status)) {
          url += `&status=${status}`;
        }

        // Append the extended URL parameters if they exist
        const extendedUrlParams = getExtendedUrlParams();
        if (extendedUrlParams) {
          url += `&${extendedUrlParams}`;
        }

        const response = await getApi<any>(url);

        if (response.success) {
          const userData: any =
            response.data || response?.data?.customerUserData || [];

          // Helper to keep original column mapping AND pass through extra fields
          const makeMapped = (item: any, idKey: "id" | "customerId" = "id") => {
            const isZeroLevel = item?.customerLevel === "zero";

            const base = columns.reduce(
              (acc, column) => {
                acc[column.key] = item[column.key] || "-";
                if (column.key === "customerName") {
                  if (isZeroLevel) {
                    acc[column.key] = CUSTOMER_TYPE?.[3]?.name;
                  }

                  // <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                  //   <span className="btnDirectCustomer">{"DIRECT Customer"}</span>
                  //   <span>{item[column.key] + "----------------"}</span>
                  // </div>
                } else if (column.key === "yourAccount") {
                  acc[column.key] = isZeroLevel
                    ? CUSTOMER_TYPE?.[3]?.name
                    : item.customerType || item[column.key];
                  // acc[column.key] = item.customerType || item[column.key];
                } else if (column?.key === "userType") {
                  acc[column.key] = item?.role || item[column.key];
                } else if (column.key === "agentType") {
                  acc["agentType"] = item[column.key]?.name;
                }
                return acc;
              },
              {
                id: item[idKey],
                customerLevel: item?.customerLevel,
                showNameInOther: item?.showNameInOther,
              } as any,
            );

            // extraFields comes from the new prop on MutliDropdown
            // Supports nested paths like "customer[0].onwardShipperId"
            if (Array.isArray(extraFields)) {
              for (const k of extraFields) {
                // Handle nested paths
                const getNestedValue = (obj: any, path: string) => {
                  return path
                    .split(/[.\[\]]/)
                    .filter(Boolean)
                    .reduce((acc, part) => {
                      return acc?.[part];
                    }, obj);
                };
                base[
                  k
                    .split(/[.\[\]]/)
                    .filter(Boolean)
                    .pop() || k
                ] = getNestedValue(item, k);
              }
            }
            return base;
          };

          // Determine which data source to use and map accordingly
          let dataToMap: any[] = [];
          let idKeyToUse: "id" | "customerId" = "id";

          if (Array.isArray(userData) && userData.length) {
            // Direct array response
            dataToMap = userData;
            idKeyToUse = "id";
          } else if (
            userData?.customerUserData &&
            Array.isArray(userData.customerUserData)
          ) {
            // Response has customerUserData array
            dataToMap = userData.customerUserData;
            idKeyToUse = "id"; // Adjust this if your API uses "customerId" instead
          } else if (
            userData?.addedByTenants &&
            Array.isArray(userData.addedByTenants)
          ) {
            // Fallback to addedByTenants (existing behavior)
            dataToMap = userData.addedByTenants;
            idKeyToUse = "customerId";
          }

          // const mappedData =
          //   Array.isArray(userData) && userData.length
          //     ? userData.map((item: any) => makeMapped(item, "id"))
          //     : (userData?.addedByTenants || []).map((item: any) =>
          //       makeMapped(item, "customerId")
          //     );

          // if (agentFind) {
          //   const agent = Array.isArray(userData)
          //     ? userData.find((item: any) => item.id === agentId)
          //     : undefined;
          //   setAgentRowData && setAgentRowData(agent);
          // }

          const mappedData = dataToMap.map((item: any) =>
            makeMapped(item, idKeyToUse),
          );

          if (agentFind) {
            const agent = Array.isArray(userData)
              ? userData.find((item: any) => item.id === agentId)
              : userData?.customerUserData?.find(
                (item: any) => item.id === agentId,
              ) ||
              userData?.addedByTenants?.find(
                (item: any) => item.id === agentId,
              ) ||
              undefined;
            setAgentRowData && setAgentRowData(agent);
          }

          if (from) {
            const updatedMappedData = mappedData.slice(1);
            setData(
              reset ? updatedMappedData : [...data, ...updatedMappedData],
            );
          } else {
            setData(reset ? mappedData : [...data, ...mappedData]);
          }

          if (response?.pagination?.totalPages) {
            const checkPage = reset ? 1 : page;
            setHasMore(checkPage < response?.pagination?.totalPages);
          } else {
            const sourceLen =
              Array.isArray(userData) && userData.length
                ? userData.length
                : userData?.addedByTenants?.length || 0;
            setHasMore(sourceLen >= limit);
          }
        } else {
          setMessage(response.error?.message || GENERAL_ERROR_MESSAGE, "error");
        }
      } catch (error) {
        console.log(GENERAL_ERROR_MESSAGE, "error");
        // setMessage(GENERAL_ERROR_MESSAGE, "error");
      } finally {
        setIsLoading(false);
        isFetching.current = false; // Reset flag after fetch
      }
    },
    [
      apiUrl,
      keyword,
      page,
      limit,
      columns,
      setData,
      data,
      setMessage,
      agentId,
      setAgentRowData,
      isLoading,
      parentCustomerId,
      extraParentId?.id,
      initialSearchKeyword,
    ],
  );

  /**
   * Effect to trigger data fetching when the keyword changes, with a debounce mechanism.
   *
   * A `staticData` list is searched in place instead — an in-memory filter has
   * no latency to debounce, and `fetchFilteredData` never learns about this
   * prop at all, so the API-driven path below is completely unaffected by it.
   */
  useEffect(() => {
    if (staticData) {
      const needle = keyword.trim().toLowerCase();
      const filtered = needle
        ? staticData.filter((item) =>
            columnsRef.current.some((column) =>
              String(item?.[column.key] ?? "")
                .toLowerCase()
                .includes(needle)
            )
          )
        : staticData;
      setDataRef.current(filtered);
      setHasMore(false);
      return;
    }
    if (keyword) {
      const debounceFetch = setTimeout(() => {
        fetchFilteredData(true);
      }, 1000);
      return () => clearTimeout(debounceFetch);
    } else {
      fetchFilteredData(true);
    }
    // `columns`/`setData` are read via ref (see above) rather than listed here —
    // most callers pass both as fresh references every render, which would
    // otherwise re-run this (and refetch) on every render of every consumer,
    // not just when the things that actually should trigger it change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, keyword, initialSearchKeyword, staticData]);

  /**
   * Effect to load more data when scrolling reaches the bottom.
   */
  useEffect(() => {
    const handleScroll = () => {
      if (dropdownRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = dropdownRef.current;
        if (
          scrollTop + clientHeight >= scrollHeight - 10 &&
          !isLoading &&
          hasMore
        ) {
          setPage((prev) => prev + 1);
        }
      }
    };

    const currentRef = dropdownRef.current;
    if (currentRef) {
      currentRef.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isLoading, hasMore]);

  /**
   * Effect to fetch data when the page number changes.
   */
  useEffect(() => {
    if (page > 1) {
      fetchFilteredData();
    }
  }, [page]);

  /**
   * Effect to fetch data when the parentCustomerId changes.
   */
  useEffect(() => {
    fetchFilteredData(true);
    if (parentCustomerId || extraParentId) {
      handleRemoveRowData();
    }
    // handleRemoveRowData();
  }, [parentCustomerId, extraParentId?.id]);

  /**
   * Pre-positions the highlight on the current selection each time the list opens, so
   * arrowing continues from where the user left off instead of jumping to the top.
   * Skipped while a search is active, where the highlight belongs on the results.
   *
   * Declared before the location re-sync below so that if a `rowData` change ever makes
   * both run in one commit, the location-specific index is the one that lands last.
   */
  useEffect(() => {
    if (!isOpened) {
      hasAutoHighlightedRef.current = false;
      // `commitSelection` stores the picked row's index within whatever list
      // was on screen at that moment (e.g. filtered search results). Left in
      // place, a later reopen without a fresh id match below would reinterpret
      // that same number against a completely different list — highlighting
      // (and, on a bare Enter, silently re-committing) an unrelated row.
      setIsSelected(null);
      return;
    }
    if (hasAutoHighlightedRef.current) return;
    // Several screens pass "" rather than null for an empty selection, and rows built by
    // `makeMapped` can carry an undefined id — which would otherwise match each other.
    if (rowData && data?.length && !keyword && rowData?.id != null) {
      const selectedIndex = data.findIndex(
        (item) => item?.id != null && String(item.id) === String(rowData.id)
      );
      if (selectedIndex !== -1) {
        setIsSelected(selectedIndex);
        hasAutoHighlightedRef.current = true;
      }
    }
  }, [isOpened, data, rowData, keyword]);

  useEffect(() => {
    if (rowData) {
      if (
        rowData.location &&
        isSelected != null &&
        data?.[isSelected] &&
        (data[isSelected].location != rowData.location ||
          data[isSelected].Location != rowData.Location)
      ) {
        const locationIndex = data.findIndex(
          (item) => item.location === rowData.location
        );
        // -1 would poison every later `items[isSelected]` lookup.
        if (locationIndex !== -1) setIsSelected(locationIndex);
      }
    }
  }, [rowData]);

  /**
   * Opens the list when the caption takes focus, so tabbing into the field is enough to
   * start browsing. Skipped for the programmatic refocus that follows an Enter selection.
   */
  const handleCaptionFocus = (e: React.FocusEvent) => {
    if (skipFocusOpenRef.current) {
      skipFocusOpenRef.current = false;
      e.stopPropagation(); // keep the wrapper from painting its focus state
      return;
    }
    // A disabled control is still a tab stop, but it must not read as active: suppress
    // both the wrapper tint and the caption's own :focus border. Stopping propagation
    // matters — otherwise focus bubbles to the wrapper's handleFocus, which sets the
    // same flag. handleBlur clears the attribute again.
    if (disable) {
      captionRef.current?.setAttribute("data-suppress-focus", "");
      e.stopPropagation();
      return;
    }
    // Mandatory-field validation focuses this control to point at its error. Opening the
    // list would cover that error, and the caption's grey :focus border outranks the red
    // one — so stay closed and suppress the focus styling, leaving the error visible.
    if (captionRef.current?.hasAttribute(VALIDATION_FOCUS_ATTR)) {
      captionRef.current.removeAttribute(VALIDATION_FOCUS_ATTR);
      captionRef.current.setAttribute("data-suppress-focus", "");
      e.stopPropagation(); // keep the wrapper from painting its focus state either
      return;
    }
    setIsDivFocused(true);
    if (!isOpened) {
      setIsOpened(true);
      setIsSearchVisible(true);
    }
  };

  const handleFocus = () => {
    if (skipFocusOpenRef.current) return;
    setIsDivFocused(true);
  };

  const handleBlur = () => {
    setIsDivFocused(false);
    captionRef.current?.removeAttribute("data-suppress-focus");
  };

  return (
    <>
      <div className="form__Field">
        {labelTitle && (
          <label className="form__label">
            {labelTitle}{" "}
            {mandatory && <span className="mandatory-mark">*</span>}
          </label>
        )}
        <div
          onFocus={handleFocus}
          onBlur={handleBlur}
          data-field={fieldKey}
          className={`customSelect ${isDivFocused ? "form__Field--focused" : ""} ${isOpened ? "open" : ""} ${error ? "customSelect--error" : ""
            }`}
          ref={outsideClickRef}
        >
          {isSearchVisible ? (
            <input
              type="text"
              placeholder="Search..."
              value={keyword}
              onChange={handleSearch}
              onKeyDown={handleKeyDown}
              className="form__input form__input--search form__input--sm"
              autoFocus
            />
          ) : (
            <div
              ref={captionRef}
              tabIndex={0}
              className={`customSelect__caption ${disable ? "customSelect__caption--disabled" : ""
                }`}
              onClick={handleOpened}
              onFocus={handleCaptionFocus}
              onKeyDown={handleKeyDown}
            //onDoubleClick={handleDoubleClick}
            >
              {rowData?.[displayColumn] ? (
                rowData?.[displayColumn]
              ) : (
                <span className="customSelect__caption__placeholder">
                  {from === "FinalizedInvoiced"
                    ? <>{captionTitles} <span style={{ color: "red" }}>*</span></>
                    : captionTitles}
                </span>
              )}

              {rowData?.[displayColumn] && enableRemove && !disable && (
                <span
                  style={{
                    background: "#fff",
                    position: "absolute",
                    right: "8px",
                    top: "10px",
                  }}
                  // The caption is focusable now, and mousedown would focus it before
                  // this click ran — opening the list that the click is about to close.
                  // Suppressing the default keeps focus off the caption entirely.
                  onMouseDown={(ev) => ev.preventDefault()}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    handleRemoveRowData();
                  }}
                >
                  <XCrossIcon />
                </span>
              )}
            </div>
          )}
          <div
            className={`customSelect__dropdown ${columns.length === 3 ? "customSelect__dropdown--width450" : ""
              }`}
            ref={dropdownRef}
          >
            <div className="customSelect__dropdown__header">
              {columns.map((column) => (
                <div
                  className={`col ${columns.length === 3 ? "col--col3" : columns.length === 4 ? "col--col4" : ""}`}
                  key={column.key}
                >
                  <h4 className="title">{column.title}</h4>
                </div>
              ))}
            </div>
            <div className="customSelect__dropdown__body">
              {isLoading && page === 1 ? (
                <div className="customSelect__dropdown__noitem">
                  <div className="col">Loading...</div>
                </div>
              ) : !data || data?.length === 0 ? (
                <div className="customSelect__dropdown__noitem">
                  <div className="col">No data found.</div>
                </div>
              ) : (
                data.map((item, index) => (
                  <div
                    className={`customSelect__dropdown__item ${rowData?.id != null && String(item?.id) === String(rowData.id) ? "selected" : ""
                      } ${isSelected === index ? "highlighted" : ""}`}
                    onClick={() => commitSelection(item, index)}
                    key={index}
                  >
                    {columns.map((column) => (
                      <div
                        key={column.key}
                        className={`col ${columns.length === 3
                          ? "col--col3"
                          : columns.length === 4
                            ? "col--col4"
                            : ""
                          }`}
                      >
                        {item[column.key]}
                      </div>
                    ))}
                  </div>
                ))
              )}
              {isLoading && page > 1 && (
                <div className="customSelect__dropdown__noitem">
                  <div className="col">Loading more...</div>
                </div>
              )}
            </div>
          </div>
        </div>
        {error && <span className="form__error">{error}</span>}
      </div >
    </>
  );
};

export default MutliDropdown;
